import { constants } from "fs/promises";
import queryString from "query-string";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ProductTileModel } from "@api/mappers/products.mapper";

import {
    CALCULATOR_KEYS,
    trackCalculatorStepStarted,
} from "@services/tracking";

import {
    CorrugatedSheetShoppingList,
    OndulineColor,
    OndulineProduct,
    OndulineProductAccessory,
    OndulineProductTileModel,
    ProductLine,
    ProductLineType,
    SlopeConfig,
} from "./corrugatedSheetCalculator.types";
import { CorrugatedSheetsCalculationConfiguration } from "./corrugatedSheetsCalculation.config";

export type CorrugatedSheetsCalculationConfig = {
    sheets: Array<OndulineProduct>;
    fixtures: Array<OndulineProduct>;
    accessories: Array<OndulineProductAccessory>;
    slopes: Array<SlopeConfig>;
};

type CalculateResultCallback = (
    height: string,
    width: string,
    length: string,
    isTwoSidedRoof: boolean,
    activeAcc: OndulineProductTileModel[],
    color?: OndulineColor,
) => CorrugatedSheetShoppingList;

type CalculateSheetsCallback = (
    surface: number,
    slope: number,
    productLine: ProductLineType,
    sidesMultiplier: number,
    accessories: Array<OndulineProductTileModel>,
) => { amount: number; hasSlopeWarning: boolean; fixtures: number };

type useCorrugatedSheetCalculationProps = {};

const filterAccessories = (
    accessory: ProductTileModel,
    ondulineAccessories: OndulineProductAccessory[],
    activeAcc: OndulineProductTileModel[],
) => {
    const acc = ondulineAccessories.find(ao => ao.sku === accessory.sku)!;
    if (acc.condition && !acc.condition(activeAcc)) {
        return false;
    }

    return true;
};

export const ConfigurationSkuList = Object.values(
    CorrugatedSheetsCalculationConfiguration,
).reduce((acc, cfg) => {
    return Array.from(
        new Set([
            ...acc,
            ...cfg.sheets.map(s => s.sku),
            ...cfg.fixtures.map(s => s.sku),
            ...cfg.accessories.map(s => s.sku),
        ]),
    );
}, [] as Array<string>);

export const useCorrugatedSheetCalculation = (
    _props: useCorrugatedSheetCalculationProps = {},
) => {
    const [activeAcc, setActiveAcc] = useState<Array<OndulineProductTileModel>>(
        [],
    );
    const [height, setHeight] = useState<string>("");
    const [width, setWidth] = useState<string>("");
    const [length, setLength] = useState<string>("");
    const [isTwoSidedRoof, setIsTwoSidedRoof] = useState<boolean>(false);
    const [color, setColor] = useState<OndulineColor>();
    const [productLine, setProductLine] = useState<ProductLineType>(
        ProductLine.Onduvilla,
    );
    const [result, setResult] = useState<CorrugatedSheetShoppingList>({
        surface: 0,
        slope: 0,
        hasSlopeWarning: false,
    });
    const [products, setProductData] = useState<Array<ProductTileModel>>([]);

    useEffect(() => {
        const q = queryString.parse(location.search, {
            parseNumbers: true,
            parseBooleans: true,
        });

        setHeight(q.roofHeight?.toString() || "");
        setWidth(q.roofWidth?.toString() || "");
        setLength(q.rootLength?.toString() || "");
        setProductLine(
            (q.productLine as ProductLineType) || ProductLine.Onduvilla,
        );
        setIsTwoSidedRoof(!!(q.isTwoSidedRoof as boolean));
        setColor(
            q.color
                ? (q.color as OndulineColor)
                : CorrugatedSheetsCalculationConfiguration[
                      ProductLine.Onduvilla
                  ].sheets.at(0)?.color,
        );
    }, []);

    const calculateSheets = useCallback<CalculateSheetsCallback>(
        (surface, slope, productLine, sidesMultiplier, accessories) => {
            const config =
                CorrugatedSheetsCalculationConfiguration[productLine];
            const currentSlope = config.slopes.find(s => {
                const below = s.below || 0;
                const above = s.above || 0;

                let result = true;
                if (below && !above) {
                    result = slope <= below;
                }
                if (below && above) result = slope <= below && slope > above;
                if (above && !below) result = slope > above;

                return result;
            });

            if (!currentSlope?.division) {
                return { amount: 0, hasSlopeWarning: false, fixtures: 0 };
            }

            const amount = Math.ceil(1.15 * (surface / currentSlope.division));
            const slopeFixtures = amount * currentSlope.fixtures;
            const accFixtures = activeAcc.reduce((acc, curr) => {
                const productAcc = accessories.find(a => a.sku == curr.sku)!;
                return (acc =
                    acc +
                    Math.ceil(
                        productAcc.amount *
                            productAcc.requiredFixtures *
                            productAcc.twoSideMultiplier,
                    ));
            }, 0);

            return {
                amount: amount * sidesMultiplier,
                hasSlopeWarning: !!currentSlope.warning,
                fixtures: slopeFixtures * sidesMultiplier + accFixtures,
            };
        },
        [width, length, productLine, isTwoSidedRoof, activeAcc],
    );

    const calculateResult = useCallback<CalculateResultCallback>(
        (height, width, length, isTwoSidedRoof, activeAcc, color) => {
            const heightNr = Number(height);
            const widthNr = Number(width);
            const lengthNr = Number(length);

            const sidesMultiplier = isTwoSidedRoof ? 2 : 1;

            const surface = widthNr * lengthNr * sidesMultiplier;
            const slope = widthNr > 0 ? (heightNr / widthNr) * 100 : 0;

            const productLineConfig =
                CorrugatedSheetsCalculationConfiguration[productLine];

            const ondulineAccessories = productLineConfig.accessories.filter(
                a => a.color === color || a.color === undefined,
            );

            const accProducts = products.filter(p =>
                ondulineAccessories.some(a => a.sku === p.sku),
            );
            const accessories = accProducts
                .filter(a =>
                    filterAccessories(a, ondulineAccessories, activeAcc),
                )
                .map(a => {
                    const acc = ondulineAccessories.find(
                        oa => oa.sku === a.sku,
                    )!;

                    const amount = Math.ceil(acc?.amountFormula(lengthNr, widthNr) || 0);
                    return {
                        ...a,
                        amount,
                        requiredFixtures: acc.requiredFixtures,
                        twoSideMultiplier: acc.twoSideMultiplier || 0,
                    } as OndulineProductTileModel;
                });

            const sheetsResult = calculateSheets(
                surface / 10_000, // in m2 ipv cm2
                slope / 100, // in decimalen ipv %
                productLine,
                sidesMultiplier,
                accessories,
            );

            const onudlineSheet = productLineConfig.sheets.find(
                s => s.color === color,
            );
            const ondulineFixture = productLineConfig.fixtures.find(
                f => f.color === color,
            );

            const sheet = products.find(p => p.sku === onudlineSheet?.sku);
            const fixture = products.find(p => p.sku === ondulineFixture?.sku);

            return {
                surface,
                slope,
                productLine: productLine,
                hasSlopeWarning: sheetsResult.hasSlopeWarning,
                sheet: {
                    ...sheet,
                    amount: sheetsResult.amount,
                } as OndulineProductTileModel,
                fixture: {
                    ...fixture,
                    amount: sheetsResult.fixtures,
                } as OndulineProductTileModel,
                accessories,
            };
        },
        [height, width, length, productLine, isTwoSidedRoof, color, activeAcc],
    );

    const hasValidResult = useMemo(() => {
        return (
            result.surface > 0 &&
            result.slope > 0 &&
            !!productLine &&
            color !== undefined &&
            (result.sheet?.amount || 0) > 0
        );
    }, [result]);

    useEffect(() => {
        setResult(
            calculateResult(
                height,
                width,
                length,
                isTwoSidedRoof,
                activeAcc,
                color,
            ),
        );
        trackCalculatorStepStarted(CALCULATOR_KEYS.corrugateSheet);
    }, [height, width, length, productLine, isTwoSidedRoof, activeAcc, color]);

    useEffect(() => {
        setColor(
            CorrugatedSheetsCalculationConfiguration[
                ProductLine.Onduvilla
            ].sheets.at(0)?.color,
        );
    }, [productLine]);

    const toggleAcc = (acc: OndulineProductTileModel) => {
        setActiveAcc(state => {
            const hasAcc = state.some(s => s.sku === acc.sku);
            return hasAcc
                ? state.filter(s => s.sku != acc.sku)
                : [...state, acc];
        });
    };

    return {
        setHeight,
        setWidth,
        setLength,
        setProductLine,
        setIsTwoSidedRoof,
        setColor,
        setProductData,
        toggleAcc,
        height,
        width,
        length,
        productLine,
        isTwoSidedRoof,
        result,
        hasValidResult,
        color,
        activeAcc,
    };
};
